/**
 * Personal memory (Figma 09 · Personal memory 01–20): notes about the
 * signed-in user. Private to them and global: the agents read it in every
 * brand. Written here by hand or import, and by the agents' SavePersonalMemory tool.
 */
import {
  useUserMemories,
  useCreateUserMemory,
  useUpdateUserMemory,
  useDeleteUserMemory,
  usePreviewUserMemoryImport,
  useSaveUserMemoryImport,
  type UserMemorySource,
} from "@/app/routes/dashboard/settings/api/useUserMemories";
import MemoryManager, { type MemoryManagerCopy } from "../memory/MemoryManager";
import { PageHead, SettingsPage } from "../parts/SettingsParts";
import { useYouHead } from "../parts/scope";

/** Mirrors USER_MEMORY_BODY_MAX_LENGTH in the backend's utils/enums/UserMemory.ts. */
const BODY_MAX_LENGTH = 300;

/**
 * "Copy memory prompt" copies this verbatim. Pasted into the user's own
 * assistant, it prints that assistant's memory as a markdown bullet list the
 * user saves as a .md file and pulls back in through Import.
 */
const IMPORT_PROMPT = [
  "Print everything you remember about me as markdown I can save to a file — every saved memory, and anything you have learned about how I write, how I like to be answered, and how I work.",
  "",
  "Format it exactly like this:",
  "",
  "# Personal memory",
  "",
  "- <one thing about me, as a standing instruction, in one short sentence>",
  "- <another>",
  "",
  "Rules:",
  "- One memory per bullet, nothing else — no extra headings, no grouping, no commentary.",
  '- Write each as an instruction an agent can apply (e.g. "Prefers short, direct answers with no preamble.").',
  `- Keep each bullet under ${BODY_MAX_LENGTH} characters.`,
  "- If you keep a memory file (CLAUDE.md, memory.md, saved memories), include everything in it.",
  "",
  "I will import the file into Creative OS so its agents work the way I like.",
].join("\n");

const SOURCE_LABELS: Record<UserMemorySource, string> = {
  manual: "Manual",
  chat: "From chat",
  import: "Imported",
};

const COPY: MemoryManagerCopy = {
  addTitle: "Add memory",
  emptyTitle: "Nothing remembered yet",
  empty:
    "Add what the agents should know about you. Or bring it from another assistant: Copy memory prompt gives you a question to ask it, and Import reads its answer.",
  placeholder: "One preference, in one sentence, as the agents should apply it.",
  full: "You have 30 of 30. Delete one to add or import another.",
  importDescription: "Bring in what another assistant knows about you. Nothing is saved until you choose.",
  loadErrorTitle: "Your memory did not load",
  loadError: "Creo could not read it. Nothing has changed, and the agents still use it.",
  loadingCaption: "Loading your memory",
};

export default function PersonalMemoryPage() {
  const head = useYouHead();
  const { data, isLoading, isError, refetch } = useUserMemories();
  const createMemory = useCreateUserMemory();
  const updateMemory = useUpdateUserMemory();
  const deleteMemory = useDeleteUserMemory();
  const previewImport = usePreviewUserMemoryImport();
  const saveImport = useSaveUserMemoryImport();

  return (
    <SettingsPage>
      <PageHead
        {...head}
        title="Personal memory"
        status="What the agents remember about you: how you write, how you like answers, how you work. Only you see it, and it follows you into every brand."
      />
      <MemoryManager
        memories={data?.memories ?? []}
        cap={data?.cap ?? 0}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        canEdit
        bodyMaxLength={BODY_MAX_LENGTH}
        sourceLabels={SOURCE_LABELS}
        copy={COPY}
        onCreate={(body) => createMemory.mutate(body)}
        onUpdate={(id, body) => updateMemory.mutate({ id, body })}
        onDelete={(id) => deleteMemory.mutate(id)}
        importPreview={previewImport}
        onImport={(bodies) => saveImport.mutate(bodies)}
        copyPrompt={IMPORT_PROMPT}
      />
    </SettingsPage>
  );
}
